import { ITextField } from '@fluentui/react/lib/TextField';
import AppContext from 'App/AppContext';
import { IPText } from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { IPTextField } from 'baseComponents/TextField/IPTextField';
import WarningMessage from 'components/Notification/WarningMessage';
import InlineSuggestions from 'components/Suggestions/InlineSuggestions';
import { globalFilterDelay } from 'globalConstants';
import { globalStackTokensGapSmall } from 'globalStyles';
import { TFunction } from 'i18next';
import { DocumentOutputPurpose } from 'models/documentOutputLocation';
import { FolderNamePreview, FolderNameToken, getOfferedFolderNameTokens } from 'models/outputLocationBinding';
import { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiPreviewFolderName } from 'services/Api/outputLocationBindingService';
import { apiRequest } from 'services/Auth/authConfig';
import { getFolderNameProblemText, getFolderNameTokenLabel } from './outputLocationTexts';

interface IFolderNameFormatFieldProps {
  purpose: DocumentOutputPurpose;
  value: string;
  tokens: FolderNameToken[];
  disabled?: boolean;
  onChange: (value: string) => void;
  // The problems of the last preview, so the host can hold back a save the server would refuse.
  onProblemsChange?: (problems: string[]) => void;
}

interface ITokenChip {
  id: string;
  label: string;
  tooltip: string;
}

/**
 * The folder name format: the text, a chip per offered token of the API's catalogue that inserts the token at the
 * caret, and a preview rendered by the server on sample values a moment after the typing stops.
 */
const FolderNameFormatField = (props: IFolderNameFormatFieldProps) => {
  const { t } = useTranslation(['outputLocations']);
  const appContext = useContext(AppContext);
  const textFieldRef = useRef<ITextField | null>(null);
  const [preview, setPreview] = useState<FolderNamePreview | undefined>(undefined);

  useEffect(() => {
    //a disabled field is not saved, so it has no problems to hold a save back
    if (props.disabled) {
      setPreview(undefined);
      props.onProblemsChange?.([]);

      return;
    }

    const timer = setTimeout(() => loadPreview(props.value), globalFilterDelay);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.value, props.disabled]);

  const loadPreview = async (format: string) => {
    try {
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const result = await apiPreviewFolderName(props.purpose, format, undefined, accessToken);
      setPreview(result);
      props.onProblemsChange?.(result.problems);
    } catch (err) {
      appContext.setError(err);
    }
  };

  // one chip per offered token of the catalogue, and for the date one per offered format;
  // a chip's id is the text it inserts
  const chips: ITokenChip[] = props.tokens.flatMap((token) => {
    const offered = getOfferedFolderNameTokens(props.purpose).find((item) => item.key === token.key);
    const label = getFolderNameTokenLabel(token.key, t as TFunction<string[]>);
    if (!offered || !label) return [];
    if (token.formats.length === 0) return [{ id: `{${token.key}}`, label, tooltip: `{${token.key}}` }];

    return token.formats
      .filter((format) => offered.formats.includes(format))
      .map((format) => ({
        id: `{${token.key}:${format}}`,
        label: `${label} (${format})`,
        tooltip: `{${token.key}:${format}}`,
      }));
  });

  const insertAtCaret = (text: string) => {
    const field = textFieldRef.current;
    const start = field?.selectionStart ?? props.value.length;
    const end = field?.selectionEnd ?? start;
    props.onChange(props.value.substring(0, start) + text + props.value.substring(end));

    //the caret goes behind the inserted token once the new value is rendered
    setTimeout(() => {
      field?.focus();
      field?.setSelectionRange(start + text.length, start + text.length);
    }, 0);
  };

  const problems = preview?.problems ?? [];

  return (
    <IPStack tokens={globalStackTokensGapSmall}>
      <IPTextField
        componentRef={textFieldRef}
        label={t('outputLocations:Evidence.Format.Label')}
        value={props.value}
        disabled={props.disabled}
        maxLength={256}
        onChange={(_ev, value) => props.onChange(value ?? '')}
      />
      {/* shown while disabled too, so the field keeps its height when a default or the rule switches it off */}
      <InlineSuggestions
        items={chips}
        onSelect={(chip) => insertAtCaret(chip.id)}
        defaultExpanded
        disabled={props.disabled}
        text={{
          label: t('outputLocations:Evidence.Token.Insert.Label'),
          hint: t('outputLocations:Evidence.Token.Insert.Hint'),
          dismiss: t('outputLocations:Evidence.Token.Insert.Dismiss'),
        }}
      />
      {problems.length > 0 && (
        <WarningMessage
          message={problems.map((problem) => getFolderNameProblemText(problem, t as TFunction<string[]>)).join(' ')}
        />
      )}
      {problems.length === 0 && preview && preview.levels.length > 0 && (
        <IPText variant="small">
          {t('outputLocations:Evidence.Format.Preview', { folder: preview.levels.join(' / ') })}
        </IPText>
      )}
    </IPStack>
  );
};

export default FolderNameFormatField;
